import { PSCType } from "@/graphql";
import type { PSCEditType } from "./PSCTypes";
import { defineAsyncComponent, markRaw, type Component } from "vue";
import ProgressSpinner from "primevue/progressspinner";
import ErrorBox from "../components/ErrorBox.vue";
import store from "@/store";

export const sectionColorClasses = {
  Plan: "bg-yellow-500",
  Do: "bg-green-500",
  Check: "bg-primary",
  Act: "surface-500",
} as const;

const availableAreas: Record<
  | "Area1"
  | "Area2"
  | "Area3"
  | "Area4a"
  | "Area4b"
  | "Area5"
  | "Area6"
  | "Area7"
  | "Area8"
  | "Area9"
  | "Area1K"
  | "Area3K"
  | "Area4K"
  | "Area6K",
  {
    key: keyof Omit<
      PSCEditType,
      "problem" | "type" | "closed" | "hashtags" | "teamLeadId" | "teamMembers" | "siteId"
    >;
    number: number;
    numberKey?: string;
    section: "Plan" | "Do" | "Check" | "Act";
    component: Component;
  }
> = {
  Area1: {
    key: "Area1",
    number: 1,
    section: "Plan",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea1Component.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area2: {
    key: "Area2",
    number: 2,
    section: "Plan",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea2Component.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area3: {
    key: "Area3",
    number: 3,
    section: "Plan",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea3Component.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area4a: {
    key: "Area4",
    number: 4,
    numberKey: "4a",
    section: "Plan",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea4aComponent.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area4b: {
    key: "Area4",
    number: 4,
    numberKey: "4b",
    section: "Plan",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea4bComponent.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area5: {
    key: "Area5",
    number: 5,
    section: "Do",
    component: defineAsyncComponent({
      loader: () => import("@/modules/psc/components/areas/PSCArea5Component.vue"),
      loadingComponent: ProgressSpinner,
      errorComponent: ErrorBox,
      delay: 200,
      timeout: 10_000,
    }),
  },
  Area6: {
    key: "Area6",
    number: 6,
    section: "Check",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea6Component.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area7: {
    key: "Area7",
    number: 7,
    section: "Act",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea7Component.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area8: {
    key: "Area8",
    number: 8,
    section: "Act",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea8Component.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area9: {
    key: "Area9",
    number: 9,
    section: "Act",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea9Component.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area1K: {
    key: "Area1K",
    number: 1,
    numberKey: "1K",
    section: "Plan",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea1KComponent.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area3K: {
    key: "Area3K",
    number: 3,
    numberKey: "3K",
    section: "Plan",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea3KComponent.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area4K: {
    key: "Area4K",
    number: 4,
    numberKey: "4K",
    section: "Plan",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea4KComponent.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
  Area6K: {
    key: "Area6K",
    number: 6,
    numberKey: "6K",
    section: "Check",
    component: markRaw(
      defineAsyncComponent({
        loader: () => import("@/modules/psc/components/areas/PSCArea6KComponent.vue"),
        loadingComponent: ProgressSpinner,
        errorComponent: ErrorBox,
        delay: 200,
        timeout: 10_000,
      }),
    ),
  },
};

const pscAreaConfig: Record<PSCType, (typeof availableAreas)[keyof typeof availableAreas][]> = {
  [PSCType.A3]: [
    availableAreas.Area1,
    availableAreas.Area2,
    availableAreas.Area3,
    availableAreas.Area4a,
    availableAreas.Area4b,
    availableAreas.Area5,
    availableAreas.Area6,
    availableAreas.Area7,
    availableAreas.Area8,
    availableAreas.Area9,
  ],
  [PSCType.A5]: [
    availableAreas.Area1,
    availableAreas.Area2,
    availableAreas.Area3,
    availableAreas.Area4a,
    availableAreas.Area4b,
    availableAreas.Area5,
    availableAreas.Area9,
  ],
  [PSCType.Kaizen]: [
    availableAreas.Area1K,
    availableAreas.Area2,
    availableAreas.Area3K,
    availableAreas.Area4K,
    availableAreas.Area5,
    availableAreas.Area6,
    // availableAreas.Area6K,
    availableAreas.Area7,
    availableAreas.Area8,
    availableAreas.Area9,
  ],
};

export default pscAreaConfig;

export function getDefaultPSC(type: PSCType): PSCEditType {
  return {
    type,
    problem: "",
    closed: false,
    hashtags: [] as string[],
    siteId: store.getters["login/preferredSite"] as number,
    teamLeadId: undefined,
    teamMembers: [] as number[],
    Area1: {
      issue: false,
      fileIds: [],
    },
    Area2: {},
    Area1K: {
      fileIds: [],
    },
    Area3: { tasks: [] },
    Area3K: {
      fileIds: [],
    },
    Area4: { reasons: [], fileIds: [] },
    Area4K: { postIts: [] },
    Area5: { tasks: [] },
    Area6: {},
    Area6K: { datapoints: [], goal: [], intervall: [] },
    Area7: { tasks: [] },
    Area8: { tasks: [] },
    Area9: { controlCircuit: false, finished: false },
  };
}
